export type UnknownRecord = Record<string, unknown>;

// check for non-primitive type, no access to arbitrary properties
export function isNonPrimitive(value: unknown): value is object {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// allows access to arbitrary properties of value
export function isUnknownRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function asUnknownRecord(value: unknown): UnknownRecord | undefined {
  return isUnknownRecord(value) ? value : undefined;
}

/** Index an object with a string key. Assumes runtime keys match the declared type — only use on closed objects. */
export function valueIn<T extends object>(obj: T, key: string): T[keyof T] | undefined {
  return (obj as Record<string, T[keyof T]>)[key];
}

/** Typed `Object.keys`. Assumes runtime keys match the declared type — only use on closed objects. */
export function keysIn<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

/** Typed `Object.entries`. Assumes runtime keys match the declared type — only use on closed objects. */
export function entriesIn<T extends object>(obj: T): Array<[keyof T, T[keyof T]]> {
  return Object.entries(obj) as Array<[keyof T, T[keyof T]]>;
}

export type Replace<T, R extends UnknownRecord> = Omit<T, keyof R> & R;

type Equals<T, U> =
  (<G>() => G extends T ? 1 : 2) extends <G>() => G extends U ? 1 : 2 ? true : false;

export type MutableProperties<T> = {
  [P in keyof T]: Equals<Pick<T, P>, Record<P, T[P]>> extends false ? never : P;
}[keyof T];

/** Return the props of T that are assignable to S */
export type FilterProps<T, S> = { [K in keyof T]: T[K] extends S ? K : never }[keyof T];
